// tooltip.ts — one delayed, styled hover tooltip. Deliberately NOT a minefield: a single floating
// element, shown on a `[data-tip]` element only after the pointer RESTS on it for DELAY ms, so
// sweeping the mouse across a row of controls never triggers a cascade of pop-ups. The tip is
// pointer-events:none (never blocks a click), positioned above the element (flipped below if there's
// no room) and clamped to the viewport. Add a tip by putting `data-tip="…"` on any element.

let tipEl: HTMLDivElement | null = null;
let showTimer: number | null = null;
let current: HTMLElement | null = null;

const DELAY = 480; // ms of rest before a tip appears — long enough that a mouse-sweep won't fire it
const GAP = 8; // px between the element and the tip

function ensure(): HTMLDivElement {
  if (!tipEl) {
    tipEl = document.createElement("div");
    tipEl.className = "tip";
    tipEl.setAttribute("role", "tooltip");
    document.body.appendChild(tipEl);
  }
  return tipEl;
}

function place(target: HTMLElement, tip: HTMLDivElement): void {
  const r = target.getBoundingClientRect();
  const tw = tip.offsetWidth;
  const th = tip.offsetHeight;
  let left = r.left + r.width / 2 - tw / 2;
  left = Math.max(GAP, Math.min(left, window.innerWidth - tw - GAP));
  let top = r.top - th - GAP; // above by default
  if (top < GAP) top = r.bottom + GAP; // flip below if there's no room above
  tip.style.left = `${Math.round(left)}px`;
  tip.style.top = `${Math.round(top)}px`;
}

function show(target: HTMLElement): void {
  const text = target.getAttribute("data-tip");
  if (!text) return;
  const tip = ensure();
  tip.textContent = text;
  place(target, tip);
  tip.classList.add("on");
}

function hide(): void {
  if (showTimer !== null) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  current = null;
  tipEl?.classList.remove("on");
}

export function initTooltips(): void {
  document.addEventListener("pointerover", (e) => {
    const t = (e.target as HTMLElement)?.closest?.<HTMLElement>("[data-tip]") ?? null;
    if (!t || t === current) return;
    // Moved onto a NEW tipped element: drop any showing tip and re-arm the delay from scratch.
    if (showTimer !== null) clearTimeout(showTimer);
    tipEl?.classList.remove("on");
    current = t;
    showTimer = window.setTimeout(() => {
      if (current === t) show(t);
    }, DELAY);
  });

  document.addEventListener("pointerout", (e) => {
    const t = (e.target as HTMLElement)?.closest?.<HTMLElement>("[data-tip]") ?? null;
    const to = e.relatedTarget as Node | null;
    // Only dismiss when the pointer genuinely LEAVES the current element (not on child transitions).
    if (t && t === current && (!to || !t.contains(to))) hide();
  });

  // A click or scroll changes the UI under the tip — dismiss immediately rather than let it linger.
  document.addEventListener("pointerdown", hide, true);
  window.addEventListener("scroll", hide, true);
}
